import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { FC, PropsWithChildren, RefObject } from 'react';
import noop from 'lodash-es/noop';
import { useBulkProductsSummary } from '../bff-api/turn-to/useBulkProductsSummary';
import { useGetTurnToQuestions } from '../bff-api/turn-to/useGetTurnToQuestions';
import { useTurnToProductData } from '../bff-api/turn-to/useTurnToProductData';
import { TURN_TO_DEFAULT_LOAD_LIMIT } from '../components/turn-to/const';
import { type QuestionSortOptions } from '../components/turn-to/types';
import { useTurnToComponentData } from './useTurnToComponentData';
import type { BulkProductSummary, QuestionTypes, ReviewTypes } from '../bff-api/turn-to/model';

interface ITurnToContext {
    reviews: ReviewTypes[];
    reviewsNumber: number;
    isLoading: boolean;
    averageRating: number;
    questions: QuestionTypes[];
    questionsOffset: number;
    questionsNumber: number;
    showMoreQuestions: () => void;
    sortQuestions: (sortOption: QuestionSortOptions) => void;
    questionsSortOption: QuestionSortOptions;
    fetchBulkProducts: (skus: string[]) => void;
    bulkProductSummary?: BulkProductSummary[];
    reviewsRef?: RefObject<HTMLDivElement> | null;
    onToggleWriteReviewModal: (isOpen: boolean) => void;
    isWriteReviewModalOpen: boolean;
}

const TurnToContext = createContext<ITurnToContext>({
    isLoading: false,
    reviews: [],
    reviewsNumber: 0,
    averageRating: 0,
    questions: [],
    showMoreQuestions: noop,
    sortQuestions: noop,
    questionsNumber: 0,
    questionsOffset: 0,
    questionsSortOption: 'mostRecent',
    fetchBulkProducts: noop,
    bulkProductSummary: [],
    reviewsRef: null,
    onToggleWriteReviewModal: noop,
    isWriteReviewModalOpen: false,
});

export type TurnToProviderProps = PropsWithChildren<{
    productId?: string;
}>;

export const TurnToProvider: FC<TurnToProviderProps> = ({ children, productId }) => {
    const { onToggleWriteReviewModal, isWriteReviewModalOpen, reviewsRef } =
        useTurnToComponentData();
    const [reviews, setReviews] = useState<ReviewTypes[]>([]);
    const [questions, setQuestions] = useState<QuestionTypes[]>([]);
    const [questionsNumber, setQuestionsNumber] = useState<number>(0);
    const [questionsOffset, setQuestionsOffset] = useState<number>(0);
    const [questionsSortOption, setQuestionsSortOption] =
        useState<QuestionSortOptions>('mostRecent');
    const [bulkProductsSKU, setBulkProductsSKU] = useState<string>('');
    const { data: reviewsData, isLoading: reviewsLoading } = useTurnToProductData(productId);
    const {
        data: bulkProductSummary,
        isLoading: bulkProductSummaryLoading,
        refetch: bulkProductSummaryRefetch,
    } = useBulkProductsSummary(bulkProductsSKU);

    const {
        data: questionData,
        isLoading: questionLoading,
        refetch: questionsRefetch,
    } = useGetTurnToQuestions(
        productId,
        TURN_TO_DEFAULT_LOAD_LIMIT,
        questionsOffset,
        questionsSortOption,
    );

    useEffect(() => {
        if (reviewsData && !reviewsLoading) {
            setReviews(reviewsData.reviewsList.reviews);
        }
    }, [reviewsData, reviewsLoading, reviews, productId]);

    useEffect(() => {
        if (questionData && !questionLoading) {
            if (questionsOffset === 0) {
                setQuestions(questionData.questions);
            } else {
                setQuestions((prevQuestions) => [...prevQuestions, ...questionData.questions]);
            }
            setQuestionsNumber(questionData.total);
        }
    }, [questionData, questionLoading, productId]);

    const showMoreQuestions = useCallback(() => {
        setQuestionsOffset((prevOffset) => prevOffset + TURN_TO_DEFAULT_LOAD_LIMIT);
    }, []);

    useEffect(() => {
        const refetchData = async () => {
            await questionsRefetch();
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        refetchData();
    }, [questionsOffset, questionsRefetch, questionsSortOption]);

    useEffect(() => {
        const refetchData = async (): Promise<void> => {
            await bulkProductSummaryRefetch();
        };

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        refetchData();
    }, [bulkProductsSKU]);

    const sortQuestions = useCallback((sortOption: QuestionSortOptions) => {
        setQuestionsOffset(0);
        setQuestionsSortOption(sortOption);
    }, []);

    const fetchBulkProducts = useCallback((skus: string[]) => {
        setBulkProductsSKU(skus.join(','));
    }, []);

    const ctx: ITurnToContext = useMemo(
        () => ({
            reviews,
            reviewsNumber: reviewsData?.productSummary?.reviews || 0,
            isLoading: reviewsLoading || questionLoading || bulkProductSummaryLoading,
            averageRating: reviewsData?.productSummary?.averageRating || 0,
            questions,
            showMoreQuestions,
            questionsOffset,
            questionsNumber,
            sortQuestions,
            questionsSortOption,
            fetchBulkProducts,
            bulkProductSummary,
            reviewsRef,
            onToggleWriteReviewModal,
            isWriteReviewModalOpen,
        }),
        [
            reviews,
            questions,
            reviewsData?.productSummary?.reviews,
            reviewsData?.productSummary?.averageRating,
            reviewsLoading,
            questionLoading,
            showMoreQuestions,
            questionsOffset,
            questionsNumber,
            sortQuestions,
            questionsSortOption,
            fetchBulkProducts,
            bulkProductSummary,
            bulkProductSummaryLoading,
            reviewsRef,
            onToggleWriteReviewModal,
            isWriteReviewModalOpen,
        ],
    );

    return <TurnToContext.Provider value={ctx}>{children}</TurnToContext.Provider>;
};

TurnToProvider.defaultProps = {
    productId: undefined,
};

export const useTurnTo = (): ITurnToContext => useContext(TurnToContext);
